import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    Button,
    TableSortLabel
} from '@mui/material';
import {
    People as PeopleIcon,
    School as CourseIcon,
    CheckCircle as CompletedIcon,
    TrendingUp as TrendingIcon,
    PersonAdd as EnrollIcon,
    ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';

interface EnrollmentStats {
    totalEnrollments: number;
    activeEnrollments: number;
    completedEnrollments: number;
    averageProgress: number;
    averageScore: number;
}

interface CourseStats {
    courseId: number;
    courseTitle: string;
    enrollmentCount: number;
    completionRate: number;
    averageScore: number;
    averageProgress: number;
}

interface RecentEnrollment {
    id: number;
    userName: string;
    courseTitle: string;
    status: string;
    progressPercentage: number;
    enrolledAt: string;
}

const EnrollmentDashboard: React.FC = () => {
    const [stats, setStats] = useState<EnrollmentStats>({
        totalEnrollments: 0,
        activeEnrollments: 0,
        completedEnrollments: 0,
        averageProgress: 0,
        averageScore: 0
    });
    const [courseStats, setCourseStats] = useState<CourseStats[]>([]);
    const [recentEnrollments, setRecentEnrollments] = useState<RecentEnrollment[]>([]);
    const [loading, setLoading] = useState(true);

    // Sorting states for course stats table
    const [courseStatsOrderBy, setCourseStatsOrderBy] = useState<'averageProgress' | 'completionRate'>('averageProgress');
    const [courseStatsOrder, setCourseStatsOrder] = useState<'asc' | 'desc'>('desc');

    // Sorting states for recent enrollments table
    const [recentOrderBy, setRecentOrderBy] = useState<'progressPercentage' | 'courseTitle'>('progressPercentage');
    const [recentOrder, setRecentOrder] = useState<'asc' | 'desc'>('desc');

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const translate = useTranslate();

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            // Load all enrollments
            const { data: enrollments } = await dataProvider.getList('course_enrollments', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'enrolledAt', order: 'DESC' },
                filter: {}
            });

            // Helper to extract course ID
            const getCourseId = (course: any): number => {
                if (typeof course === 'number') return course;
                if (course && typeof course === 'object' && course.id) return course.id;
                if (typeof course === 'string') {
                    const parts = course.split('/');
                    return parseInt(parts[parts.length - 1]);
                }
                return 0;
            };

            // Helper to extract user ID
            const getUserId = (userId: any): number => {
                if (typeof userId === 'number') return userId;
                if (typeof userId === 'string') {
                    const parts = userId.split('/');
                    return parseInt(parts[parts.length - 1]);
                }
                return 0;
            };

            // Extract unique IDs
            const courseIds = Array.from(new Set(enrollments.map((e: any) => getCourseId(e.course)).filter(id => id > 0)));
            const userIds = Array.from(new Set(enrollments.map((e: any) => getUserId(e.userId)).filter(id => id > 0)));

            // Load details in parallel
            const [coursesData, usersData] = await Promise.all([
                courseIds.length > 0 ? dataProvider.getMany('courses', { ids: courseIds }) : Promise.resolve({ data: [] }),
                userIds.length > 0 ? dataProvider.getMany('users', { ids: userIds }) : Promise.resolve({ data: [] })
            ]);

            // Create maps
            const coursesMap = new Map(coursesData.data.map((c: any) => [c.id, c]));
            const usersMap = new Map(usersData.data.map((u: any) => [u.id, u]));

            // Enrich enrollments
            const enrichedEnrollments = enrollments.map((enrollment: any) => {
                const courseId = getCourseId(enrollment.course);
                const userId = getUserId(enrollment.userId);
                const course = coursesMap.get(courseId);
                const user = usersMap.get(userId);
                const fullName = user ? ((user.firstName || '') + ' ' + (user.lastName || '')).trim() : '';

                return {
                    ...enrollment,
                    course: course || { id: courseId, title: 'Unbekannter Kurs' },
                    userName: enrollment.userName || fullName || user?.email || ('User ' + userId)
                };
            });

            // Calculate stats
            const totalEnrollments = enrichedEnrollments.length;
            const activeEnrollments = enrichedEnrollments.filter(
                (e: any) => e.status === 'in_progress' || e.status === 'enrolled'
            ).length;
            const completedEnrollments = enrichedEnrollments.filter((e: any) => e.status === 'completed').length;

            const averageProgress = enrichedEnrollments.length > 0
                ? enrichedEnrollments.reduce((sum: number, e: any) => sum + (e.progressPercentage || 0), 0) / enrichedEnrollments.length
                : 0;

            const completedScores = enrichedEnrollments.filter((e: any) => e.finalScore !== null && e.finalScore !== undefined);
            const averageScore = completedScores.length > 0
                ? completedScores.reduce((sum: number, e: any) => sum + e.finalScore, 0) / completedScores.length
                : 0;

            setStats({
                totalEnrollments,
                activeEnrollments,
                completedEnrollments,
                averageProgress: Math.round(averageProgress),
                averageScore: Math.round(averageScore)
            });

            // Calculate course stats
            const courseMap = new Map<number, any>();

            for (const enrollment of enrichedEnrollments as any[]) {
                const courseId = enrollment.course?.id;
                if (!courseId) continue;

                if (!courseMap.has(courseId)) {
                    courseMap.set(courseId, {
                        courseId,
                        courseTitle: enrollment.course.title,
                        enrollments: [],
                        completed: 0,
                        totalProgress: 0,
                        totalScore: 0,
                        scoreCount: 0
                    });
                }

                const courseData = courseMap.get(courseId);
                courseData.enrollments.push(enrollment);
                courseData.totalProgress += enrollment.progressPercentage || 0;

                if (enrollment.status === 'completed') {
                    courseData.completed++;
                }

                if (enrollment.finalScore !== null && enrollment.finalScore !== undefined) {
                    courseData.totalScore += enrollment.finalScore;
                    courseData.scoreCount++;
                }
            }

            const courseStatsArray: CourseStats[] = Array.from(courseMap.values()).map((data) => ({
                courseId: data.courseId,
                courseTitle: data.courseTitle,
                enrollmentCount: data.enrollments.length,
                completionRate: data.enrollments.length > 0
                    ? parseFloat(((data.completed / data.enrollments.length) * 100).toFixed(2))
                    : 0,
                averageScore: data.scoreCount > 0
                    ? parseFloat((data.totalScore / data.scoreCount).toFixed(2))
                    : 0,
                averageProgress: data.enrollments.length > 0
                    ? parseFloat((data.totalProgress / data.enrollments.length).toFixed(2))
                    : 0
            }));

            setCourseStats(courseStatsArray);

            // Load recent enrollments - user data should already be in enrollment
            const recentEnrollmentsData = (enrichedEnrollments.slice(0, 10) as any[]).map((enrollment) => ({
                id: enrollment.id,
                userName: enrollment.userName,
                courseTitle: enrollment.course?.title || 'Unbekannter Kurs',
                status: enrollment.status,
                progressPercentage: enrollment.progressPercentage,
                enrolledAt: enrollment.enrolledAt
            }));

            setRecentEnrollments(recentEnrollmentsData);

        } catch (error) {
            notify('Fehler beim Laden der Dashboard-Daten', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'completed':
                return 'Abgeschlossen';
            case 'in_progress':
                return 'In Bearbeitung';
            case 'failed':
                return 'Nicht bestanden';
            case 'enrolled':
                return 'Eingeschrieben';
            default:
                return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'in_progress':
                return 'primary';
            case 'failed':
                return 'error';
            case 'enrolled':
                return 'default';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPercentage = (value: number | undefined | null) => {
        if (value === null || value === undefined || isNaN(value)) {
            return '0.00';
        }
        return Number(value).toFixed(2);
    };

    // Sorting handlers for course stats
    const handleCourseStatsSort = (property: 'averageProgress' | 'completionRate') => {
        const isAsc = courseStatsOrderBy === property && courseStatsOrder === 'asc';
        setCourseStatsOrder(isAsc ? 'desc' : 'asc');
        setCourseStatsOrderBy(property);
    };

    // Sorting handlers for recent enrollments
    const handleRecentSort = (property: 'progressPercentage' | 'courseTitle') => {
        const isAsc = recentOrderBy === property && recentOrder === 'asc';
        setRecentOrder(isAsc ? 'desc' : 'asc');
        setRecentOrderBy(property);
    };

    // Sort course stats
    const sortedCourseStats = [...courseStats].sort((a, b) => {
        const aValue = a[courseStatsOrderBy];
        const bValue = b[courseStatsOrderBy];
        return courseStatsOrder === 'asc'
            ? aValue - bValue
            : bValue - aValue;
    });

    // Sort recent enrollments
    const sortedRecentEnrollments = [...recentEnrollments].sort((a, b) => {
        if (recentOrderBy === 'progressPercentage') {
            const aValue = a.progressPercentage || 0;
            const bValue = b.progressPercentage || 0;
            return recentOrder === 'asc'
                ? aValue - bValue
                : bValue - aValue;
        } else {
            // Sort by courseTitle
            return recentOrder === 'asc'
                ? a.courseTitle.localeCompare(b.courseTitle)
                : b.courseTitle.localeCompare(a.courseTitle);
        }
    });

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Lade Dashboard...</Typography>
                <LinearProgress sx={{ mt: 2 }} />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Button startIcon={<BackIcon />} onClick={() => navigate('/enrollments/manage')}>
                    Zurück zur Einschreibungsverwaltung
                </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    {translate('enrollments.dashboard.title')}
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<EnrollIcon />}
                    onClick={() => navigate('/enrollments/manage')}
                >
                    {translate('enrollments.dashboard.enrollUser')}
                </Button>
            </Box>

            {/* Stats Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 4 }}>
                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <PeopleIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                            <Box>
                                <Typography variant="h4">{stats.totalEnrollments}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('enrollments.dashboard.stats.total')}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CourseIcon color="info" sx={{ fontSize: 40, mr: 2 }} />
                            <Box>
                                <Typography variant="h4">{stats.activeEnrollments}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('enrollments.dashboard.stats.active')}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <CompletedIcon color="success" sx={{ fontSize: 40, mr: 2 }} />
                            <Box>
                                <Typography variant="h4">{stats.completedEnrollments}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('enrollments.dashboard.stats.completed')}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TrendingIcon color="warning" sx={{ fontSize: 40, mr: 2 }} />
                            <Box>
                                <Typography variant="h4">{stats.averageProgress}%</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('enrollments.dashboard.stats.avgProgress')}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Course Statistics */}
            <Card sx={{ mb: 4 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {translate('enrollments.dashboard.courseStats.title')}
                    </Typography>
                    {courseStats.length === 0 ? (
                        <Alert severity="info">{translate('enrollments.dashboard.courseStats.noData')}</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('enrollments.dashboard.courseStats.table.course')}</TableCell>
                                        <TableCell align="right">{translate('enrollments.dashboard.courseStats.table.enrollments')}</TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={courseStatsOrderBy === 'completionRate'}
                                                direction={courseStatsOrderBy === 'completionRate' ? courseStatsOrder : 'asc'}
                                                onClick={() => handleCourseStatsSort('completionRate')}
                                            >
                                                {translate('enrollments.dashboard.courseStats.table.completionRate')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={courseStatsOrderBy === 'averageProgress'}
                                                direction={courseStatsOrderBy === 'averageProgress' ? courseStatsOrder : 'asc'}
                                                onClick={() => handleCourseStatsSort('averageProgress')}
                                            >
                                                {translate('enrollments.dashboard.courseStats.table.avgProgress')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">{translate('enrollments.dashboard.courseStats.table.avgScore')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedCourseStats.map((course) => (
                                        <TableRow key={course.courseId}>
                                            <TableCell>{course.courseTitle}</TableCell>
                                            <TableCell align="right">{course.enrollmentCount}</TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={course.completionRate}
                                                        sx={{ width: 80 }}
                                                    />
                                                    <Typography variant="body2">
                                                        {formatPercentage(course.completionRate)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">{formatPercentage(course.averageProgress)}%</TableCell>
                                            <TableCell align="right">
                                                {course.averageScore > 0 ? `${formatPercentage(course.averageScore)}%` : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Recent Enrollments */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Letzte Einschreibungen
                    </Typography>
                    {recentEnrollments.length === 0 ? (
                        <Alert severity="info">Keine Einschreibungen vorhanden.</Alert>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Benutzer</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={recentOrderBy === 'courseTitle'}
                                                direction={recentOrderBy === 'courseTitle' ? recentOrder : 'asc'}
                                                onClick={() => handleRecentSort('courseTitle')}
                                            >
                                                Kurs
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={recentOrderBy === 'progressPercentage'}
                                                direction={recentOrderBy === 'progressPercentage' ? recentOrder : 'asc'}
                                                onClick={() => handleRecentSort('progressPercentage')}
                                            >
                                                Fortschritt
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>Eingeschrieben am</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedRecentEnrollments.map((enrollment) => (
                                        <TableRow key={enrollment.id}>
                                            <TableCell>{enrollment.userName}</TableCell>
                                            <TableCell>{enrollment.courseTitle}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(enrollment.status)}
                                                    color={getStatusColor(enrollment.status) as any}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={enrollment.progressPercentage || 0}
                                                        sx={{ width: 100 }}
                                                    />
                                                    <Typography variant="body2">
                                                        {formatPercentage(enrollment.progressPercentage || 0)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{formatDate(enrollment.enrolledAt)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default EnrollmentDashboard;
